import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Eye, Loader2, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { apiErrorMessage } from '../../pages/admin/transactions/utils/formatters';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Investor portal "visible up to" date: one global date + per-investor
 * overrides (backend: routers/portal_visibility.py, services/portal_visibility.py).
 * Investors see their figures only up to this date; reconciliation carries on daily.
 */
export function InvestorPortalDateCard({ token }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [data, setData] = useState(null);
  const [globalDate, setGlobalDate] = useState('');
  const [pickInvestor, setPickInvestor] = useState('');
  const [pickDate, setPickDate] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/admin/investor-portal-visibility`, { headers });
      setData(r.data);
      setGlobalDate(r.data.visible_until || '');
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Could not load the investor portal date'));
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [token]);

  const saveGlobal = async (value) => {
    setBusy(true);
    try {
      await axios.put(`${API_URL}/api/admin/investor-portal-visibility`, { visible_until: value || null }, { headers });
      toast.success(value ? `Investors now see data up to ${value}` : 'Investors now see live data');
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Save failed'));
    } finally { setBusy(false); }
  };

  const saveOverride = async (userId, value) => {
    setBusy(true);
    try {
      await axios.put(`${API_URL}/api/admin/investor-portal-visibility/investor/${userId}`, { visible_until: value || null }, { headers });
      toast.success(value ? `Override set to ${value}` : 'Override removed — follows the global date');
      setPickInvestor('');
      setPickDate('');
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Save failed'));
    } finally { setBusy(false); }
  };

  const label = (r) => `${r.rp_code ? `${r.rp_code} · ` : ''}${r.name}`;

  return (
    <Card data-testid="investor-portal-date-card">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2"><Eye className="h-5 w-5" /> Investor portal date</CardTitle>
        <CardDescription>
          Investors see their figures only up to this date: dashboard, ledger, reports, holdings and their own
          Deal Room investments and payouts. You can keep reconciling daily; nothing after the date shows until you
          move it forward. Leave it blank to show live data. Statements are unaffected.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {!data ? <Loader2 className="h-4 w-4 animate-spin" /> : (
          <>
            <div className="flex flex-wrap items-end gap-2">
              <div>
                <p className="text-xs text-slate-600 mb-1">All investors see data up to</p>
                <Input type="date" value={globalDate} onChange={(e) => setGlobalDate(e.target.value)}
                  className="w-44" data-testid="portal-date-global" />
              </div>
              <Button size="sm" onClick={() => saveGlobal(globalDate)} disabled={busy || globalDate === (data.visible_until || '')}
                data-testid="portal-date-global-save">Save</Button>
              {data.visible_until && (
                <Button size="sm" variant="outline" onClick={() => saveGlobal('')} disabled={busy}>Show live data</Button>
              )}
            </div>
            <p className="text-xs text-slate-500">
              {data.visible_until ? `Currently ${data.visible_until}` : 'Currently live (no date set)'}
              {data.updated_by_email ? ` · last changed by ${data.updated_by_email}` : ''}
            </p>

            <div className="border-t pt-3 space-y-2">
              <p className="text-sm font-medium">Per-investor overrides</p>
              <p className="text-xs text-slate-600">Pin one investor to a different date (e.g. their statement went out on another day).</p>
              {data.overrides.length === 0 && <p className="text-xs text-slate-500">None — everyone follows the date above.</p>}
              {data.overrides.map(o => (
                <div key={o.id} className="flex items-center justify-between text-sm border-b pb-1">
                  <span>{label(o)}</span>
                  <span className="flex items-center gap-2">
                    <strong>{o.visible_until}</strong>
                    <Button size="sm" variant="ghost" onClick={() => saveOverride(o.id, '')} disabled={busy} title="Remove override">
                      <X className="h-4 w-4" />
                    </Button>
                  </span>
                </div>
              ))}
              <div className="flex flex-wrap items-end gap-2">
                <Select value={pickInvestor} onValueChange={setPickInvestor}>
                  <SelectTrigger className="w-72" data-testid="portal-date-override-investor"><SelectValue placeholder="Choose investor" /></SelectTrigger>
                  <SelectContent>
                    {data.investors.map(r => <SelectItem key={r.id} value={r.id}>{label(r)}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input type="date" value={pickDate} onChange={(e) => setPickDate(e.target.value)} className="w-44"
                  data-testid="portal-date-override-date" />
                <Button size="sm" variant="outline" onClick={() => saveOverride(pickInvestor, pickDate)}
                  disabled={busy || !pickInvestor || !pickDate}>Set override</Button>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default InvestorPortalDateCard;
